import React, { useContext, useState } from 'react';
import { UserData, UsersContext } from '../providers/UsersProvider';
import '../localization/config';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { DataTable } from '@performant-software/semantic-components';
import Page, { ICrumb } from '../components/Page';
import ContentBox from '../components/ContentBox';
import { Button, Icon } from 'semantic-ui-react';
import AddUserModal from '../components/AddUserModal';
import { FlexRow } from '../layouts';
import ConfirmationModal from '../components/ConfirmationModal';

export interface IUsersProps {}

const Users = (_props: IUsersProps) => {
  const { users, onCreateUser, onUpdateUser, onDeleteUser } =
    useContext(UsersContext);

  const { t } = useTranslation();

  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [activeUser, setActiveUser] = useState<UserData | undefined>();
  const [confirmTitle, setConfirmTitle] = useState<string>('');
  const [confirmMessage, setConfirmMessage] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const renderCheck = (key: string, item: any) => {
    return <div>{item[key] ? <Icon name="check" color="green" /> : ''}</div>;
  };

  const handleAddUser = () => {
    setActiveUser({
      firstName: '',
      lastName: '',
      email: '',
      projectAdmin: false,
      systemAdmin: false,
      userAdmin: false,
    });
    setAddOpen(true);
  };

  const handleSave = (item: UserData) => {
    if (!item.id) {
      onCreateUser(item);
    } else {
      onUpdateUser(item);
    }
    setAddOpen(false);
  };

  const handleEdit = (item: UserData) => {
    setActiveUser(item);
    setAddOpen(true);
  };

  const handleDelete = (item: UserData) => {
    setActiveUser(item);
    setConfirmTitle(t('users.confirmUserDeletion'));
    setConfirmMessage(
      `${t('users.doYouWishToDeleteUser')} ${item.firstName} ${
        item.lastName
      } ${t('general.?')}`
    );
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    onDeleteUser(activeUser?.id as number);
    setConfirmOpen(false);
    setActiveUser(undefined);
  };

  const actions = [
    { name: 'edit', onClick: handleEdit },
    { name: 'delete', onClick: handleDelete },
  ];

  const columns = [
    {
      name: 'lastName',
      label: t('users.lastName'),
      sortable: false,
    },
    {
      name: 'firstName',
      label: t('users.firstName'),
      sortable: false,
    },
    {
      name: 'email',
      label: t('users.email'),
      sortable: false,
    },
    {
      name: 'systemAdmin',
      label: t('users.systemAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('systemAdmin', item),
    },
    {
      name: 'projectAdmin',
      label: t('users.projectAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('projectAdmin', item),
    },
    {
      name: 'userAdmin',
      label: t('users.userAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('userAdmin', item),
    },
  ];

  const crumbs: ICrumb[] = [
    {
      key: 'users',
      route: '/users',
      content: t('users.users'),
      active: true,
      onClick: () => {},
    },
  ];

  return (
    <Page crumbs={crumbs}>
      <ContentBox widthPct={100} heightPct={80} padding={32}>
        <FlexRow padBottom={10}>
          <Button onClick={handleAddUser} color="green">
            <Icon name="add" />
            {t('general.add')}
          </Button>
        </FlexRow>
        <DataTable
          actions={actions}
          columns={columns}
          items={users}
          tableProps={{ width: '100%' }}
        />
        <AddUserModal
          open={addOpen}
          initialValues={activeUser as UserData}
          onSubmit={handleSave}
          onCancel={() => setAddOpen(false)}
        />
        <ConfirmationModal
          title={confirmTitle}
          message={confirmMessage}
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      </ContentBox>
    </Page>
  );
};

export default Users;
